import React from 'react';
import type { StepConfig } from '../../types';

type UnknownRecord = Record<string, unknown>;

type FormInstance = {
  instanceId?: string;
  data?: UnknownRecord;
  lastStepIndex?: number;
};

type CompletedForms = Record<string, FormInstance[]>;

type Props = {
  values?: UnknownRecord;
  completedForms?: CompletedForms;
  allSteps: StepConfig[];
};

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function isPlainObject(v: unknown): v is UnknownRecord {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'string') return v.trim() === '' ? '—' : v;
  if (typeof v === 'number') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

export function getSectionTitleByGroupKey(
  allSteps: StepConfig[],
  groupKey: string
): string | undefined {
  for (const step of allSteps) {
    if (step.sections) {
      for (const section of step.sections) {
        if (section.cards) {
          for (const card of section.cards) {
            if (card.groupKey === groupKey) {
              return section.title;
            }
          }
        }
      }
    }
  }
  return undefined;
}

export function getCardTitleByGroupKey(
  allSteps: StepConfig[],
  groupKey: string
): string | undefined {
  for (const step of allSteps) {
    if (step.sections) {
      for (const section of step.sections) {
        if (section.cards) {
          for (const card of section.cards) {
            if (card.groupKey === groupKey) {
              return card.title;
            }
          }
        }
        // Legacy: check if section has groupKey matching (for backward compatibility)
        const sectionGroupKey = section.groupKey || section.id;
        if (sectionGroupKey === groupKey) {
          return section.title;
        }
      }
    }
  }
  return undefined;
}
export function getFieldLabelByFieldId(
  allSteps: StepConfig[],
  fieldId: string
): string | undefined {
  for (const step of allSteps) {
    if (step.fieldGroups) {
      for (const fieldGroup of step.fieldGroups) {
        if (fieldGroup.fields) {
          for (const field of fieldGroup.fields) {
            if (field.id === fieldId) {
              return field.label;
            }
          }
        }
      }
    }
  }
  return undefined;
}
const WillCapacityYesEmailBody = ({ values, allSteps }: Props) => {
  const safeValues = isPlainObject(values) ? values : {};
  return (
    <table
      role="presentation"
      width="100%"
      border={0}
      cellPadding="0"
      cellSpacing="0"
      align="center"
    >
      <tbody>
        <tr>
          <td
            colSpan={2}
            style={{ backgroundColor: '#0c1b39', color: '#ffffff', padding: '10px' }}
            width="100%"
            align="left"
          >
            Capacity Concern
          </td>
        </tr>
        <tr>
          <td
            colSpan={2}
            style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
            width="100%"
            align="left"
          >
            {typeof safeValues.isCapacityConcern === 'string' ? safeValues.isCapacityConcern : ''}
          </td>
        </tr>
        <tr>
          <td
            style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
            width="30%"
            align="left"
          >
            {escapeHtml(fmt(getFieldLabelByFieldId(allSteps, 'capacityConcernFirstName')))}
          </td>
          <td
            style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
            width="70%"
            align="left"
          >
            {typeof safeValues.capacityConcernFirstName === 'string'
              ? escapeHtml(fmt(safeValues.capacityConcernFirstName))
              : ''}
          </td>
        </tr>
        <tr>
          <td colSpan={2} width="100%">
            {typeof safeValues.capacityConcernMiddleName === 'string' &&
              safeValues.capacityConcernMiddleName && (
                <React.Fragment key="capacityConcernMiddleName">
                  <table width="100%" border={0} cellPadding={0} cellSpacing={0} align="left">
                    <tbody>
                      <tr>
                        <td
                          style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
                          width="30%"
                          align="left"
                        >
                          {escapeHtml(
                            fmt(getFieldLabelByFieldId(allSteps, 'capacityConcernMiddleName'))
                          )}
                        </td>
                        <td
                          style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
                          width="70%"
                          align="left"
                        >
                          {typeof safeValues.capacityConcernMiddleName === 'string'
                            ? escapeHtml(fmt(safeValues.capacityConcernMiddleName))
                            : ''}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </React.Fragment>
              )}
          </td>
        </tr>
        <tr>
          <td
            style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
            width="30%"
            align="left"
          >
            {escapeHtml(fmt(getFieldLabelByFieldId(allSteps, 'capacityConcernLastName')))}
          </td>
          <td
            style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
            width="70%"
            align="left"
          >
            {typeof safeValues.capacityConcernLastName === 'string'
              ? escapeHtml(fmt(safeValues.capacityConcernLastName))
              : ''}
          </td>
        </tr>
        <tr>
          <td
            style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
            width="30%"
            align="left"
          >
            {escapeHtml(fmt(getFieldLabelByFieldId(allSteps, 'capacityConcernEmail')))}
          </td>
          <td
            style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
            width="70%"
            align="left"
          >
            {typeof safeValues.capacityConcernEmail === 'string'
              ? escapeHtml(fmt(safeValues.capacityConcernEmail))
              : ''}
          </td>
        </tr>
        <tr>
          <td
            style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
            width="30%"
            align="left"
          >
            {escapeHtml(fmt(getFieldLabelByFieldId(allSteps, 'capacityConcernPhone')))}
          </td>
          <td
            style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
            width="70%"
            align="left"
          >
            {typeof safeValues.capacityConcernPhone === 'string'
              ? escapeHtml(fmt(safeValues.capacityConcernPhone))
              : ''}
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default WillCapacityYesEmailBody;
